const PALETTE_ID = 'ds-command-palette';
const RECENT_KEY = 'ds-palette-recent';
const RECENT_MAX = 5;
const DEBOUNCE_MS = 200;

class CommandPalette {
    constructor(root) {
        this.root = root;
        this.input = root.querySelector('[data-ds-palette-input]');
        this.results = root.querySelector('[data-ds-palette-results]');
        this.status = root.querySelector('[data-ds-palette-status]');
        this.config = JSON.parse(root.querySelector('[data-ds-palette-config]').textContent);
        this.options = [];
        this.activeIndex = -1;
        this.debounce = null;
        this.request = null;

        this.bind();
    }

    bind() {
        this.root.addEventListener('shown.bs.modal', () => {
            this.input.focus();
            this.input.select();
            if (!this.input.value.trim()) {
                this.renderIdle();
            }
        });

        this.root.addEventListener('hidden.bs.modal', () => this.abort());

        this.input.addEventListener('input', () => {
            clearTimeout(this.debounce);
            this.debounce = setTimeout(() => this.run(this.input.value.trim()), DEBOUNCE_MS);
        });

        this.input.addEventListener('keydown', (event) => this.onKeydown(event));

        this.results.addEventListener('mousemove', (event) => {
            const option = event.target.closest('[data-palette-option]');
            if (option) {
                this.setActive(this.options.indexOf(option));
            }
        });

        this.results.addEventListener('click', (event) => {
            const recent = event.target.closest('[data-palette-recent]');
            if (recent) {
                event.preventDefault();
                this.input.value = recent.dataset.paletteRecent;
                this.run(this.input.value);
                this.input.focus();
            }
        });
    }

    abort() {
        this.request?.abort();
        this.request = null;
        clearTimeout(this.debounce);
    }

    onKeydown(event) {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.setActive(this.activeIndex + 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.setActive(this.activeIndex - 1);
                break;
            case 'Home':
                if (this.options.length) {
                    event.preventDefault();
                    this.setActive(0);
                }
                break;
            case 'End':
                if (this.options.length) {
                    event.preventDefault();
                    this.setActive(this.options.length - 1);
                }
                break;
            case 'Enter': {
                const option = this.options[this.activeIndex];
                if (option) {
                    event.preventDefault();
                    this.remember(this.input.value.trim());
                    window.location.href = option.href;
                }
                break;
            }
            default:
                break;
        }
    }

    setActive(index) {
        if (!this.options.length) {
            this.activeIndex = -1;
            this.input.removeAttribute('aria-activedescendant');
            return;
        }

        const total = this.options.length;
        this.activeIndex = ((index % total) + total) % total;

        this.options.forEach((option, position) => {
            const isActive = position === this.activeIndex;
            option.classList.toggle('is-active', isActive);
            option.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        const active = this.options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
    }

    async run(term) {
        this.abort();

        if (term.length < this.config.minLength) {
            this.renderIdle();
            return;
        }

        this.renderLoading();

        this.request = new AbortController();

        try {
            const response = await fetch(`${this.config.endpoint}?q=${encodeURIComponent(term)}`, {
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                signal: this.request.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const payload = await response.json();

            // Een trager antwoord op een oudere term mag het huidige resultaat niet overschrijven.
            if (payload.term === this.input.value.trim()) {
                this.render(payload);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.renderMessage('fas fa-triangle-exclamation', this.config.strings.error);
            }
        }
    }

    render(payload) {
        this.results.innerHTML = '';
        this.options = [];
        this.activeIndex = -1;

        if (!payload.total) {
            this.renderMessage(
                'fas fa-magnifying-glass',
                this.config.strings.empty.replace('__TERM__', payload.term),
                this.config.strings.emptyHint,
            );
            this.announce(this.count(0));
            return;
        }

        payload.groups.forEach((group) => {
            this.results.append(this.renderGroup(group, payload.term));
        });

        this.input.setAttribute('aria-expanded', 'true');
        this.announce(this.count(payload.total));
        this.setActive(0);
    }

    renderGroup(group, term) {
        const wrapper = document.createElement('div');
        wrapper.className = 'ds-palette__group';

        const title = document.createElement('p');
        title.className = 'ds-palette__group-title';
        title.textContent = group.label;
        wrapper.append(title);

        group.results.forEach((result) => {
            const option = document.createElement('a');
            option.className = 'ds-palette__option';
            option.id = `ds-palette-option-${this.options.length}`;
            option.href = result.url;
            option.setAttribute('role', 'option');
            option.setAttribute('aria-selected', 'false');
            option.dataset.paletteOption = '';

            const icon = document.createElement('span');
            icon.className = `ds-palette__option-icon ${group.icon}`;
            icon.setAttribute('aria-hidden', 'true');

            const text = document.createElement('span');
            text.className = 'ds-palette__option-text';

            const titleEl = document.createElement('span');
            titleEl.className = 'ds-palette__option-title';
            titleEl.append(highlight(result.title, term));
            text.append(titleEl);

            if (result.subtitle) {
                const subtitle = document.createElement('span');
                subtitle.className = 'ds-palette__option-subtitle';
                subtitle.append(highlight(result.subtitle, term));
                text.append(subtitle);
            }

            const enter = document.createElement('span');
            enter.className = 'ds-palette__option-enter fas fa-arrow-turn-down-left';
            enter.setAttribute('aria-hidden', 'true');

            option.append(icon, text, enter);
            option.addEventListener('click', () => this.remember(term));

            wrapper.append(option);
            this.options.push(option);
        });

        return wrapper;
    }

    renderLoading() {
        this.results.innerHTML = '';
        this.options = [];

        for (let index = 0; index < 4; index += 1) {
            const row = document.createElement('div');
            row.className = 'ds-palette__skeleton';
            this.results.append(row);
        }
    }

    renderIdle() {
        this.results.innerHTML = '';
        this.options = [];
        this.activeIndex = -1;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');

        const recent = this.recent();

        if (!recent.length) {
            this.renderMessage('fas fa-keyboard', this.config.strings.idle);
            return;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'ds-palette__group';

        const title = document.createElement('p');
        title.className = 'ds-palette__group-title';
        title.textContent = this.config.strings.recent;
        wrapper.append(title);

        recent.forEach((term) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'ds-palette__option';
            button.dataset.paletteRecent = term;

            const icon = document.createElement('span');
            icon.className = 'ds-palette__option-icon fas fa-clock-rotate-left';
            icon.setAttribute('aria-hidden', 'true');

            const label = document.createElement('span');
            label.className = 'ds-palette__option-title';
            label.textContent = term;

            button.append(icon, label);
            wrapper.append(button);
        });

        this.results.append(wrapper);
    }

    renderMessage(icon, message, hint = null) {
        this.results.innerHTML = '';
        this.options = [];
        this.activeIndex = -1;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');

        const wrapper = document.createElement('div');
        wrapper.className = 'ds-palette__message';

        const iconEl = document.createElement('span');
        iconEl.className = `ds-palette__message-icon ${icon}`;
        iconEl.setAttribute('aria-hidden', 'true');

        const text = document.createElement('p');
        text.className = 'ds-palette__message-text';
        text.textContent = message;

        wrapper.append(iconEl, text);

        if (hint) {
            const hintEl = document.createElement('p');
            hintEl.className = 'ds-palette__message-hint';
            hintEl.textContent = hint;
            wrapper.append(hintEl);
        }

        this.results.append(wrapper);
    }

    count(total) {
        const template = total === 1 ? this.config.strings.countOne : this.config.strings.countMany;
        return template.replace('__COUNT__', total);
    }

    announce(message) {
        this.status.textContent = message;
    }

    recent() {
        try {
            const stored = JSON.parse(localStorage.getItem(RECENT_KEY));
            return Array.isArray(stored) ? stored.slice(0, RECENT_MAX) : [];
        } catch {
            return [];
        }
    }

    remember(term) {
        if (!term) {
            return;
        }

        const terms = [term, ...this.recent().filter((value) => value !== term)].slice(0, RECENT_MAX);

        try {
            localStorage.setItem(RECENT_KEY, JSON.stringify(terms));
        } catch {
            // Private mode: geschiedenis is een extraatje, geen vereiste.
        }
    }
}

/**
 * Bouwt de tekst met <mark> op via DOM-nodes; de zoekterm mag nooit als HTML landen.
 */
function highlight(text, term) {
    const fragment = document.createDocumentFragment();
    const needle = term.trim().toLowerCase();
    const haystack = text.toLowerCase();

    if (!needle || !haystack.includes(needle)) {
        fragment.append(document.createTextNode(text));
        return fragment;
    }

    let cursor = 0;
    let match = haystack.indexOf(needle);

    while (match !== -1) {
        fragment.append(document.createTextNode(text.slice(cursor, match)));

        const mark = document.createElement('mark');
        mark.textContent = text.slice(match, match + needle.length);
        fragment.append(mark);

        cursor = match + needle.length;
        match = haystack.indexOf(needle, cursor);
    }

    fragment.append(document.createTextNode(text.slice(cursor)));

    return fragment;
}

function isTypingTarget(element) {
    if (!element) {
        return false;
    }

    return (
        element.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
    );
}

function init() {
    const root = document.getElementById(PALETTE_ID);

    // De instantie hangt aan het element zelf, zodat opnieuw initialiseren na een
    // Livewire-navigatie geen dubbele listeners oplevert.
    if (root && !root.dsPalette) {
        root.dsPalette = new CommandPalette(root);
    }

    if (!navigator.platform.toUpperCase().includes('MAC')) {
        document
            .querySelectorAll('[data-ds-shortcut-key]')
            .forEach((element) => {
                element.textContent = 'Ctrl';
            });
    }
}

document.addEventListener('keydown', (event) => {
    const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
    const isSlash = event.key === '/' && !isTypingTarget(document.activeElement);

    if (!isShortcut && !isSlash) {
        return;
    }

    const root = document.getElementById(PALETTE_ID);

    if (!root) {
        return;
    }

    event.preventDefault();
    init();
    window.dsOpenModal(PALETTE_ID);
});

document.addEventListener('DOMContentLoaded', init);
document.addEventListener('livewire:navigated', init);
